import creditCardValidator from 'card-validator';
import { CardType } from '~client/model/card-type';
import { keepNumbersOnly } from '~common/helper/string';

export interface CreditCardExpirationDate {
  month: number;
  year: number;
}

export const creditCard = {
  isExpired: (month: number, year: number): boolean =>
    !!month && !!year && !creditCard.isValidExpirationDate(month, year),

  isValidExpirationDate: (month: number, year: number): boolean =>
    creditCardValidator.expirationDate({ month: month.toString(), year: year.toString() }).isValid,

  isValidNumber: (creditCardNumber: string): boolean => {
    const validation = creditCardValidator.number(creditCardNumber);
    const card = validation.card;
    return card && creditCard.isKnownType(card) && validation.isValid;
  },

  isValidCVC: (cvc: string, creditCardNumber: string): boolean => {
    const valid = creditCardValidator.number(creditCardNumber);
    const cardType = creditCard.parseCardType(creditCardNumber);
    if (valid.isValid) {
      return creditCard.isValidCVCByCardType(cvc, cardType);
    }
    return false;
  },

  isValidCVCByCardType: (cvc: string, cardType: CardType): boolean => {
    return cardType === CardType.AMEX
      ? creditCardValidator.cvv(cvc, 4).isValid
      : creditCardValidator.cvv(cvc).isValid;
  },

  formatCVC: (cvc: string, cardType: CardType): string => {
    if (cvc) {
      return keepNumbersOnly(cvc).slice(0, cardType === CardType.AMEX ? 4 : 3);
    }
    return cvc;
  },

  formatCardNumber: (value: string): string => {
    if (!value) {
      return value;
    }
    const cleanNumber = keepNumbersOnly(value);
    const card = creditCardValidator.number(cleanNumber).card;
    if (!card || !creditCard.isKnownType(card)) {
      return cleanNumber;
    }
    let formattedNumber = cleanNumber;
    for (let i = 0; i < card.gaps.length; i++) {
      const index = card.gaps[i] + i;
      if (index < formattedNumber.length) {
        formattedNumber = `${formattedNumber.substr(0, index)} ${formattedNumber.substr(index)}`;
      }
    }
    return formattedNumber;
  },

  formatExpirationDate: (value: string): string => {
    if (value) {
      // clear the string to get only numbers first
      const expirationDate = keepNumbersOnly(value).substr(0, 4);
      if (expirationDate.length === 4) {
        return `${expirationDate.substr(0, 2)}/${expirationDate.substr(2)}`;
      }
      return expirationDate;
    }
    return value;
  },

  parseCardType: (value: string): CardType => {
    const card = creditCardValidator.number(value).card;
    if (card) {
      return creditCard.getCardType(card);
    }
    return CardType.UNKNOWN;
  },

  getCardType: (card: any & { type: CardType }): CardType => {
    switch (card.type) {
      case 'visa':
        return CardType.VISA;
      case 'mastercard':
        return CardType.MASTERCARD;
      case 'american-express':
        return CardType.AMEX;
      default:
        return CardType.UNKNOWN;
    }
  },

  isKnownType: (card: any & { type: CardType }): boolean =>
    creditCard.getCardType(card) !== CardType.UNKNOWN,

  parseCardExpiry: (value: string): CreditCardExpirationDate => {
    const expirationDate = creditCardValidator.expirationDate(value);
    return {
      month: Number.parseInt(expirationDate.month),
      year: 2000 + Number.parseInt(expirationDate.year),
    };
  },
};



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/common/src/helper/credit-card.ts